import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArabicText } from "@/components/ArabicText";
import Quiz from "@/components/Quiz";
import { Eye, Type as TypeIcon, CheckCircle2, XCircle, RotateCcw, ArrowLeft } from "lucide-react";
import { t } from "@/i18n";

// Strip Arabic diacritics (harakat) when the switch is off.
const stripHarakat = (s) => s.replace(/[\u064B-\u0652\u0670]/g, "");

export default function ReadingDetail() {
  const { rid } = useParams();
  const [data, setData] = useState(null);
  const [showVowels, setShowVowels] = useState(true);
  const [showTranslit, setShowTranslit] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [phase, setPhase] = useState("read");
  const [done, setDone] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get(`/content/reading/${rid}`).then((r) => setData(r.data)); }, [rid]);
  if (!data) return <div className="text-muted-foreground">{t("lessoneditor.chargement")}</div>;

  const quiz = data.quiz.map((q) => ({ ...q, skill: "reading", ref: rid }));

  const onDone = async (r) => {
    setDone(r);
    await api.post(`/progress/reading/${rid}?score=${Math.round((r.correct / r.total) * 100)}`).catch(() => {});
    setPhase("done");
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="h-32 rounded-2xl bg-gradient-to-br from-skill-reading/30 to-skill-reading/5 flex items-center justify-center mb-4">
        <ArabicText size="text-5xl" className="text-skill-reading">{data.title_ar}</ArabicText>
      </div>
      <h1 className="font-head text-2xl font-bold">{data.title_fr}</h1>
      <p className="text-muted-foreground mb-4">{data.desc}</p>

      {phase === "read" && (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <button data-testid="toggle-vowels" onClick={() => setShowVowels((v) => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${showVowels ? "bg-primary text-primary-foreground border-primary" : ""}`}>
              <Eye size={15} /> {t("readingdetail.voyelles_diacritiques")}
            </button>
            <button data-testid="toggle-translit" onClick={() => setShowTranslit((v) => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${showTranslit ? "bg-primary text-primary-foreground border-primary" : ""}`}>
              <TypeIcon size={15} /> {t("lessoneditor.translitteration")}
            </button>
            <button data-testid="toggle-all-trans" onClick={() => setShowAll((v) => !v)} className="px-3 py-2 rounded-xl border text-sm">
              {showAll ? t("readingdetail.masquer") : t("readingdetail.afficher")} {t("readingdetail.toutes_les_traductions")}
            </button>
          </div>

          <div className="space-y-3 mb-6">
            {data.sentences.map((s, i) => (
              <div key={i} data-testid={`sentence-${i}`} onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))}
                className="rounded-2xl border bg-card p-4 cursor-pointer hover:border-primary transition">
                <div className="arabic text-3xl" dir="rtl">{showVowels ? s.ar : stripHarakat(s.ar)}</div>
                {(showAll || revealed[i]) && <div className="text-muted-foreground mt-2 text-sm">{s.fr}</div>}
                {!showAll && !revealed[i] && <div className="text-xs text-muted-foreground mt-1">{t("readingdetail.cliquer_pour_traduire")}</div>}
              </div>
            ))}
          </div>

          <button data-testid="do-quiz" onClick={() => setPhase("quiz")}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition">{t("readingdetail.faire_le_quiz")}</button>
        </>
      )}

      {phase === "quiz" && <Quiz questions={quiz} onDone={onDone} />}

      {phase === "done" && done && (() => {
        const score = Math.round((done.correct / done.total) * 100);
        const passed = score >= 70;
        return (
          <div className="rounded-2xl border bg-card p-8 text-center" data-testid="reading-done">
            {passed
              ? <CheckCircle2 size={52} className="mx-auto mb-3" style={{ color: "#16A34A" }} />
              : <XCircle size={52} className="mx-auto mb-3 text-destructive" />}
            <h2 className="font-head text-2xl font-bold mb-1">{t("readingdetail.lecture_validee")}</h2>
            <p className="text-muted-foreground mb-4">
              {passed ? t("readingdetail.bonne_comprehension_continue_avec_d_autres") : t("readingdetail.relis_le_texte_et_retente_le")}
            </p>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-muted-foreground mb-1">
                <span>{done.correct} correctes</span>
                <span>{done.total - done.correct} {t("lessonpage.ratees")}</span>
              </div>
              <div className="h-3 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${score}%`, backgroundColor: passed ? "#16A34A" : "hsl(var(--destructive))" }} />
              </div>
            </div>
            <div className="text-4xl font-head font-bold mb-1" style={{ color: passed ? "#16A34A" : undefined }}>{score}%</div>
            <div className="text-sm text-muted-foreground mb-6">{done.correct} / {done.total} {t("interexerciserecap.bonnes_reponses")}</div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => { setPhase("quiz"); setDone(null); }}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition">
                <RotateCcw size={16} /> {t("dailyplansession.refaire_le_quiz")}
              </button>
              <button onClick={() => { setPhase("read"); setDone(null); }}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition">
                {t("readingdetail.relire_le_texte")}
              </button>
              <button onClick={() => nav("/practice/reading")}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition">
                <ArrowLeft size={16} /> {t("readingdetail.autres_textes")}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
